import { useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { getAuthMeQueryKey } from '@/api/generated/endpoints/auth/auth';
import { ApiProblem, http } from '@/api/http';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { problemMessage } from '@/i18n/problem';
import { OutboxContext, type NewOp, type Outbox } from '@/offline/context';
import {
  loadOutbox,
  newKey,
  outcomeOf,
  saveOutbox,
  type OutboxOp,
  type SendOutcome,
} from '@/offline/outbox';
import { refreshProductCache } from '@/offline/productCache';
import { invalidatePath } from '@/lib/queryCache';

const RETRY_MS = 15_000;

async function send(op: OutboxOp): Promise<{ outcome: SendOutcome; error?: ApiProblem }> {
  try {
    await http.request({
      url: op.url,
      method: 'POST',
      data: op.body,
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': op.key },
    });
    return { outcome: 'sent' };
  } catch (e) {
    const problem =
      e instanceof ApiProblem ? e : new ApiProblem(0, { title: String(e), code: 'network' });
    return { outcome: outcomeOf(problem.status), error: problem };
  }
}

/** Очередь операций без связи (ADR-0009): хранит, отправляет по порядку, сообщает о сбоях. */
export function OutboxProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();
  const userId = user?.id ?? null;
  const [ops, setOps] = useState<OutboxOp[]>([]);
  const [online, setOnline] = useState(() => navigator.onLine);
  const [sending, setSending] = useState(false);
  const busy = useRef(false);
  const opsRef = useRef(ops);
  opsRef.current = ops;

  useEffect(() => {
    const loaded = userId === null ? [] : loadOutbox(userId);
    opsRef.current = loaded; // отправка ниже (тот же проход эффектов) должна видеть очередь
    setOps(loaded);
  }, [userId]);

  const commit = useCallback(
    (next: OutboxOp[]) => {
      opsRef.current = next;
      setOps(next);
      if (userId !== null && !saveOutbox(userId, next)) {
        void message.error(t('offline.storageFailed'));
      }
    },
    [userId, message, t],
  );

  const flush = useCallback(async () => {
    if (busy.current || userId === null) return;
    busy.current = true;
    setSending(true);
    let sent = 0;
    try {
      // Строго по порядку: приёмка, сделанная раньше, уходит раньше.
      for (const op of opsRef.current.filter((o) => o.state === 'queued')) {
        const { outcome, error } = await send(op);
        if (outcome === 'stop') {
          if (error?.status === 0) setOnline(false);
          break;
        }
        setOnline(true);
        if (outcome === 'sent') {
          sent += 1;
          commit(opsRef.current.filter((o) => o.key !== op.key));
        } else {
          commit(
            opsRef.current.map((o) =>
              o.key === op.key
                ? {
                    ...o,
                    state: 'failed' as const,
                    error: error ? problemMessage(error) : '',
                    errorCode: error?.code ?? 'unknown',
                  }
                : o,
            ),
          );
        }
      }
    } finally {
      busy.current = false;
      setSending(false);
    }
    if (sent) {
      void message.success(t('offline.sent', { count: sent }));
      void refreshProductCache(); // приёмка могла создать новый товар
      await Promise.all(
        ['/api/v1/batches', '/api/v1/stock', '/api/v1/shipments', '/api/v1/products'].map((p) =>
          invalidatePath(queryClient, p),
        ),
      );
    }
  }, [userId, commit, message, t, queryClient]);

  // Связь вернулась, страница открылась, раз в 15 с — пробуем отправить.
  useEffect(() => {
    const up = () => {
      setOnline(true);
      // Запустились без связи под последним вошедшим — теперь проверить вход на сервере.
      void queryClient.refetchQueries({ queryKey: getAuthMeQueryKey(), type: 'active' });
      void flush();
      void refreshProductCache(); // за время без связи могли появиться товары
    };
    const down = () => {
      setOnline(false);
    };
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    const timer = window.setInterval(() => {
      if (opsRef.current.some((o) => o.state === 'queued')) void flush();
    }, RETRY_MS);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
      window.clearInterval(timer);
    };
  }, [flush, queryClient]);

  useEffect(() => {
    if (userId === null) return;
    void flush();
    // Справочник товаров — для выбора без связи (T-184).
    void refreshProductCache().then((result) => {
      if (result === 'network') setOnline(false); // запустились без связи с сервером
    });
  }, [userId, flush]);

  const value = useMemo<Outbox>(() => {
    const enqueue = (op: NewOp): OutboxOp => {
      const item: OutboxOp = {
        ...op,
        key: newKey(),
        createdAt: new Date().toISOString(),
        state: 'queued',
      };
      commit([...opsRef.current, item]);
      return item;
    };
    return {
      online,
      ops,
      queued: ops.filter((o) => o.state === 'queued'),
      failed: ops.filter((o) => o.state === 'failed'),
      sending,
      enqueue,
      markOffline: () => {
        setOnline(false);
      },
      flush,
      retry: (key, body) => {
        commit(
          opsRef.current.map((o): OutboxOp => {
            if (o.key !== key) return o;
            // Исправленные данные — новая операция: старый ключ сервер с ними не примет.
            return {
              ...o,
              ...(body ? { body, key: newKey() } : {}),
              state: 'queued',
              error: undefined,
              errorCode: undefined,
            };
          }),
        );
        void flush();
      },
      discard: (key) => {
        commit(opsRef.current.filter((o) => o.key !== key));
      },
    };
  }, [online, ops, sending, commit, flush]);

  return <OutboxContext.Provider value={value}>{children}</OutboxContext.Provider>;
}
